import { defineMessages, type IntlShape } from 'react-intl';
import Community from '@alltrails/denali/icons/Community';
import Globe from '@alltrails/denali/icons/Globe';
import Lock from '@alltrails/denali/icons/Lock';
import { PrivacyPolicySubjects } from '@alltrails/types/PrivacyPolicySubjects';
import { VISIBILITY_FOLLOWERS_ONLY, VISIBILITY_PRIVATE, VISIBILITY_PUBLIC } from '@alltrails/types/Visibility';
import type { RadioOptions } from '../types/RadioOptions';

const privacyOptions = defineMessages({
  PUBLIC: {
    defaultMessage: 'Public'
  },
  FOLLOWERS_ONLY: {
    defaultMessage: 'Followers only'
  },
  PRIVATE: {
    defaultMessage: 'Private'
  }
});

export const getVisibilityOption = (option: string, intl: IntlShape) => {
  switch (option) {
    case VISIBILITY_PUBLIC:
      return intl.formatMessage(privacyOptions.PUBLIC);
    case VISIBILITY_FOLLOWERS_ONLY:
      return intl.formatMessage(privacyOptions.FOLLOWERS_ONLY);
    case VISIBILITY_PRIVATE:
      return intl.formatMessage(privacyOptions.PRIVATE);
    default:
      return '';
  }
};

export const getPrivacyIcon = (privacyOption: string) => {
  switch (privacyOption) {
    case VISIBILITY_PUBLIC:
      return <Globe />;
    case VISIBILITY_FOLLOWERS_ONLY:
      return <Community size="md" />;
    case VISIBILITY_PRIVATE:
      return <Lock size="md" />;
    default:
      return <></>;
  }
};

const listPrivacySubText = defineMessages({
  THIS_LIST_ANYONE: {
    defaultMessage: 'This list is viewable by anyone.'
  },
  THIS_LIST_FOLLOWERS: {
    defaultMessage: 'This list is only visible to your followers.'
  },
  THIS_LIST_COLLABORATORS: {
    defaultMessage: 'This list is only visible to you and any collaborators.'
  }
});

export const getListPrivacyOptions = (
  intl: IntlShape,
  isCollabList?: boolean,
  isAddingCollaboratorsToFollowersOnlyList?: boolean
): RadioOptions[] => {
  const listPrivacyOptions = [
    {
      id: 'public',
      name: 'public',
      value: VISIBILITY_PUBLIC,
      title: getVisibilityOption(VISIBILITY_PUBLIC, intl),
      subtext: intl.formatMessage(listPrivacySubText.THIS_LIST_ANYONE),
      icon: getPrivacyIcon(VISIBILITY_PUBLIC)
    },
    {
      id: 'followersOnly',
      name: 'followersOnly',
      value: VISIBILITY_FOLLOWERS_ONLY,
      title: getVisibilityOption(VISIBILITY_FOLLOWERS_ONLY, intl),
      subtext: intl.formatMessage(listPrivacySubText.THIS_LIST_FOLLOWERS),
      icon: getPrivacyIcon(VISIBILITY_FOLLOWERS_ONLY)
    },
    {
      id: 'onlyMe',
      name: 'onlyMe',
      value: VISIBILITY_PRIVATE,
      title: getVisibilityOption(VISIBILITY_PRIVATE, intl),
      subtext: intl.formatMessage(listPrivacySubText.THIS_LIST_COLLABORATORS),
      icon: getPrivacyIcon(VISIBILITY_PRIVATE)
    }
  ];
  if (isCollabList || isAddingCollaboratorsToFollowersOnlyList) {
    return [listPrivacyOptions[0], listPrivacyOptions[2]];
  }
  return listPrivacyOptions;
};

const activityPrivacySubText = defineMessages({
  THIS_ACTIVITY_ANYONE: {
    defaultMessage: 'This activity is visible to anyone. It may appear on trail pages, in community feeds and on your profile.'
  },
  THIS_ACTIVITY_FOLLOWERS: {
    defaultMessage: 'This activity is only visible to your followers. It will appear in your followers feeds and on your profile.'
  },
  THIS_ACTIVITY_YOU: {
    defaultMessage: 'This activity is only visible to you.'
  }
});

export const getActivityPrivacyOptions = (intl: IntlShape): RadioOptions[] => [
  {
    id: 'public',
    name: 'public',
    value: VISIBILITY_PUBLIC,
    title: getVisibilityOption(VISIBILITY_PUBLIC, intl),
    subtext: intl.formatMessage(activityPrivacySubText.THIS_ACTIVITY_ANYONE),
    icon: getPrivacyIcon(VISIBILITY_PUBLIC)
  },
  {
    id: 'followersOnly',
    name: 'followersOnly',
    value: VISIBILITY_FOLLOWERS_ONLY,
    title: getVisibilityOption(VISIBILITY_FOLLOWERS_ONLY, intl),
    subtext: intl.formatMessage(activityPrivacySubText.THIS_ACTIVITY_FOLLOWERS),
    icon: getPrivacyIcon(VISIBILITY_FOLLOWERS_ONLY)
  },
  {
    id: 'onlyMe',
    name: 'onlyMe',
    value: VISIBILITY_PRIVATE,
    title: getVisibilityOption(VISIBILITY_PRIVATE, intl),
    subtext: intl.formatMessage(activityPrivacySubText.THIS_ACTIVITY_YOU),
    icon: getPrivacyIcon(VISIBILITY_PRIVATE)
  }
];

const customMapPrivacySubText = defineMessages({
  THIS_MAP_ANYONE: {
    defaultMessage: 'This map is visible by anyone.'
  },
  THIS_MAP_FOLLOWERS: {
    defaultMessage: 'This map is only visible to your followers.'
  },
  THIS_MAP_YOU: {
    defaultMessage: 'This map is only visible to you.'
  }
});

export const getCustomMapPrivacyOptions = (intl: IntlShape): RadioOptions[] => [
  {
    id: 'public',
    name: 'public',
    value: VISIBILITY_PUBLIC,
    title: getVisibilityOption(VISIBILITY_PUBLIC, intl),
    subtext: intl.formatMessage(customMapPrivacySubText.THIS_MAP_ANYONE),
    icon: getPrivacyIcon(VISIBILITY_PUBLIC)
  },
  {
    id: 'followersOnly',
    name: 'followersOnly',
    value: VISIBILITY_FOLLOWERS_ONLY,
    title: getVisibilityOption(VISIBILITY_FOLLOWERS_ONLY, intl),
    subtext: intl.formatMessage(customMapPrivacySubText.THIS_MAP_FOLLOWERS),
    icon: getPrivacyIcon(VISIBILITY_FOLLOWERS_ONLY)
  },
  {
    id: 'onlyMe',
    name: 'onlyMe',
    value: VISIBILITY_PRIVATE,
    title: getVisibilityOption(VISIBILITY_PRIVATE, intl),
    subtext: intl.formatMessage(customMapPrivacySubText.THIS_MAP_YOU),
    icon: getPrivacyIcon(VISIBILITY_PRIVATE)
  }
];

const customRotePrivacySubText = defineMessages({
  THIS_ROUTE_ANYONE: {
    defaultMessage: 'This route is visible to anyone'
  },
  THIS_ROUTE_FOLLOWERS: {
    defaultMessage: 'This route is only visible to your followers'
  },
  THIS_ROUTE_YOU: {
    defaultMessage: 'This route is only visible to you'
  }
});

export const getCustomRoutePrivacyOptions = (intl: IntlShape): RadioOptions[] => [
  {
    id: 'public',
    name: 'public',
    value: VISIBILITY_PUBLIC,
    title: getVisibilityOption(VISIBILITY_PUBLIC, intl),
    subtext: intl.formatMessage(customRotePrivacySubText.THIS_ROUTE_ANYONE),
    icon: getPrivacyIcon(VISIBILITY_PUBLIC)
  },
  {
    id: 'followersOnly',
    name: 'followersOnly',
    value: VISIBILITY_FOLLOWERS_ONLY,
    title: getVisibilityOption(VISIBILITY_FOLLOWERS_ONLY, intl),
    subtext: intl.formatMessage(customRotePrivacySubText.THIS_ROUTE_FOLLOWERS),
    icon: getPrivacyIcon(VISIBILITY_FOLLOWERS_ONLY)
  },
  {
    id: 'onlyMe',
    name: 'onlyMe',
    value: VISIBILITY_PRIVATE,
    title: getVisibilityOption(VISIBILITY_PRIVATE, intl),
    subtext: intl.formatMessage(customRotePrivacySubText.THIS_ROUTE_YOU),
    icon: getPrivacyIcon(VISIBILITY_PRIVATE)
  }
];

const listMessages = defineMessages({
  title: { defaultMessage: 'List privacy' },
  description: { defaultMessage: 'Choose who can view this list.' }
});

const activityMessages = defineMessages({
  title: { defaultMessage: 'Activity privacy' },
  description: { defaultMessage: 'Choose who can view this activity and where it will appear.' }
});

const customMapMessages = defineMessages({
  title: { defaultMessage: 'Map privacy' },
  description: { defaultMessage: 'Choose who can view this map.' }
});

const customRouteMessages = defineMessages({
  title: { defaultMessage: 'Change privacy level' },
  description: { defaultMessage: 'Choose who can view this route' }
});

export const getPrivacyModalOptions = (
  contentType: string,
  intl: IntlShape,
  isCollabList?: boolean,
  isAddingCollaboratorsToFollowersOnlyList?: boolean
) => {
  // create a switch statement that populates different options based on the content type passed in
  switch (contentType) {
    case PrivacyPolicySubjects.LISTS: {
      return {
        title: intl.formatMessage(listMessages.title),
        description: intl.formatMessage(listMessages.description),
        radioOptions: getListPrivacyOptions(intl, isCollabList, isAddingCollaboratorsToFollowersOnlyList)
      };
    }
    case PrivacyPolicySubjects.ACTIVITIES: {
      return {
        title: intl.formatMessage(activityMessages.title),
        description: intl.formatMessage(activityMessages.description),
        radioOptions: getActivityPrivacyOptions(intl)
      };
    }
    case PrivacyPolicySubjects.CUSTOM_MAP: {
      return {
        title: intl.formatMessage(customMapMessages.title),
        description: intl.formatMessage(customMapMessages.description),
        radioOptions: getCustomMapPrivacyOptions(intl)
      };
    }
    case PrivacyPolicySubjects.CUSTOM_ROUTE: {
      return {
        title: intl.formatMessage(customRouteMessages.title),
        description: intl.formatMessage(customRouteMessages.description),
        radioOptions: getCustomRoutePrivacyOptions(intl)
      };
    }
    default: {
      return { title: '', description: '', radioOptions: [] };
    }
  }
};
